/* Censure — local draft shapes and the helpers that turn edits into API calls.
   Discouraged themes are rows on the server (create / patch / delete); banned
   words are one list saved whole. Ban themes are read-only here. */

import { api } from '../../../../api/client';
import type { CensureTopicTheme } from '../../../../api/client';

export type Sec = 'words' | 'themes' | 'bans';
export const SECS: Sec[] = ['words', 'themes', 'bans'];

/** A discouraged theme as edited. `id` is null until the server has created it. */
export interface Theme { key: string; id: number | null; name: string; prompt: string; on: boolean }
type Stored = { name: string; prompt: string; on: boolean };

let seq = 0;
export const newTheme = (): Theme => ({ key: `new-${Date.now()}-${(seq += 1)}`, id: null, name: '', prompt: '', on: true });
export const themeOf = (r: CensureTopicTheme): Theme => ({ key: `srv-${r.id}`, id: r.id, name: r.topic_heading, prompt: r.detection_prompt, on: r.is_active });

/** Why a theme can't be saved yet, or null. The server needs a name and a
 *  prompt, and names must be unique on the account. */
export function themeProblem(t: Theme, all: Theme[]): 'incomplete' | 'duplicate' | null {
  const n = t.name.trim();
  if (!n || !t.prompt.trim()) return 'incomplete';
  if (all.some((o) => o.key !== t.key && o.name.trim() === n)) return 'duplicate';
  return null;
}

/**
 * Keeps the server in step with the discouraged-themes draft. `stored` is what
 * the server holds, by id, and is updated after every call, so a failed save
 * retries only what's left. Themes that can't be saved yet are skipped until
 * they're fixed. Returns the ids the server gave to new themes, by key.
 */
export function themeSync(accountId: string, initial: Theme[]) {
  const stored = new Map<number, Stored>(initial.filter((t) => t.id !== null).map((t) => [t.id as number, { name: t.name, prompt: t.prompt, on: t.on }]));
  // New themes by key, so a save that runs before the id reaches the draft
  // patches the row instead of creating it twice.
  const made = new Map<string, number>();
  return async (draft: Theme[]): Promise<Record<string, number>> => {
    const idOf = (t: Theme) => t.id ?? made.get(t.key) ?? null;
    const live = new Set(draft.map(idOf).filter((id): id is number => id !== null));
    for (const id of [...stored.keys()]) {
      if (live.has(id)) continue;
      await api.deleteCensureNotPreferableTheme(accountId, id);
      stored.delete(id);
    }
    const created: Record<string, number> = {};
    for (const t of draft) {
      if (themeProblem(t, draft)) continue;
      const next: Stored = { name: t.name.trim(), prompt: t.prompt.trim(), on: t.on };
      const id = idOf(t);
      if (id === null) {
        const row = await api.createCensureNotPreferableTheme(accountId, { topic_heading: next.name, detection_prompt: next.prompt, is_active: next.on });
        stored.set(row.id, next);
        made.set(t.key, row.id);
        created[t.key] = row.id;
        continue;
      }
      const prev = stored.get(id);
      if (prev && prev.name === next.name && prev.prompt === next.prompt && prev.on === next.on) continue;
      await api.patchCensureNotPreferableTheme(accountId, id, { topic_heading: next.name, detection_prompt: next.prompt, is_active: next.on });
      stored.set(id, next);
    }
    return created;
  };
}

/** Split "a, b,c" into new lowercase words, keeping the order typed. */
export function parseWords(input: string, existing: string[]) {
  const parts = [...new Set(input.split(/[\n,]+/).map((x) => x.trim().toLowerCase()).filter(Boolean))];
  const have = new Set(existing);
  return { fresh: parts.filter((x) => !have.has(x)), dup: parts.filter((x) => have.has(x)) };
}
